import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  IconButton,
  Image,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  useDisclosure
} from "@chakra-ui/react";
import {
  FaBars,
  FaBell,
  FaBuilding,
  FaHome,
  FaMapMarkedAlt,
  FaSignOutAlt,
  FaUser,
  FaVideo
} from "react-icons/fa";
import Home from "./Home";
import MapaCameras from "../components/MapaCameras";
import Ocorrencias from "../components/Ocorrencias";
import PerfilUsuario from "../components/PerfilUsuario";
import GerenciarCameras from "../components/GerenciarCameras";
import InformacoesEmpresa from "../components/InformacoesEmpresa";
import CitySelector from "../components/CitySelector";
import { api } from "../api";
import { useBranding } from "../context/BrandingContext";

const normalizeCity = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();

const resolveCityState = (city) => {
  const explicitState = String(city?.estado || city?.uf || "").trim().toUpperCase();
  if (explicitState) return explicitState;
  const normalizedCity = normalizeCity(city?.cidade);
  if (normalizedCity === "caldas" || normalizedCity === "cabo verde") {
    return "MG";
  }
  return "";
};

const DashboardLoader = ({ label = "Carregando ambiente..." }) => (
  <Box px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
    <Stack spacing={4} maxW="980px" mx="auto" mb={6}>
      <Skeleton height="28px" width="180px" borderRadius="full" startColor="rgba(255,255,255,0.10)" endColor="rgba(255,255,255,0.22)" />
      <Skeleton height={{ base: "36px", md: "52px" }} width={{ base: "240px", md: "420px" }} borderRadius="18px" startColor="rgba(255,255,255,0.10)" endColor="rgba(255,255,255,0.22)" />
      <Text color="whiteAlpha.900" fontWeight="700">
        {label}
      </Text>
    </Stack>
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 6 }} maxW="980px" mx="auto">
      {Array.from({ length: 4 }).map((_, index) => (
        <Box
          key={index}
          minH={{ base: "240px", md: "290px" }}
          borderRadius="28px"
          overflow="hidden"
          bg="rgba(16, 38, 27, 0.58)"
          border="1px solid rgba(235, 255, 241, 0.16)"
          boxShadow="0 24px 60px rgba(0, 0, 0, 0.20)"
          p={{ base: 4, md: 5 }}
        >
          <Stack h="100%" justify="space-between">
            <Stack spacing={3}>
              <Skeleton height="26px" width="140px" borderRadius="full" startColor="rgba(255,255,255,0.10)" endColor="rgba(255,255,255,0.22)" />
              <Skeleton height={{ base: "34px", md: "46px" }} width="70%" borderRadius="16px" startColor="rgba(255,255,255,0.10)" endColor="rgba(255,255,255,0.22)" />
              <Skeleton height="18px" width="86%" borderRadius="12px" startColor="rgba(255,255,255,0.10)" endColor="rgba(255,255,255,0.22)" />
            </Stack>
            <Skeleton height="40px" width="170px" borderRadius="full" startColor="rgba(255,255,255,0.10)" endColor="rgba(255,255,255,0.22)" />
          </Stack>
        </Box>
      ))}
    </SimpleGrid>
  </Box>
);

const Dashboard = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedOption, setSelectedOption] = useState(
    () => localStorage.getItem("dashboard_target_tab") || "home"
  );
  const [forceCitySelector, setForceCitySelector] = useState(false);
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "{}"), []);
  const userId = user?.id;
  const apiBase = api.defaults.baseURL?.replace(/\/api$/, "") || "";
  const {
    loading,
    branding,
    currentVisual,
    availableCities,
    selectedCity,
    setSelectedCity,
    refreshBranding,
    assetUrl,
    canSwitchCities
  } = useBranding();

  const logoUrl = assetUrl(branding.logo_url) || "/sistema/logo.png";
  const brandName = branding.display_name || "EU Monitoro";
  const shouldShowCitySelector = availableCities.length > 1;
  const shouldBlockUntilCityChoice = loading || forceCitySelector || (shouldShowCitySelector && !selectedCity);
  const headerBg = `linear-gradient(120deg, ${branding.secondary_color || "#0b6530"}, ${branding.primary_color || "#2f855a"})`;
  const dashboardBackgroundUrl =
    assetUrl(currentVisual?.background_image_url) ||
    assetUrl(currentVisual?.home_image_url) ||
    assetUrl(branding.home_background_url) ||
    "/sistema/fundo.png";

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("active_empresa_id");
    localStorage.removeItem("branding_city");
    navigate("/sistema");
  };

  useEffect(() => {
    const target = localStorage.getItem("dashboard_target_tab");
    if (target) {
      setSelectedOption(target);
      localStorage.removeItem("dashboard_target_tab");
    }
  }, []);

  useEffect(() => {
    refreshBranding(localStorage.getItem("branding_city") || "");
  }, [refreshBranding]);

  const handleMenuClick = (option) => {
    setSelectedOption(option);
    onClose();
  };

  const handleSelectCity = (city) => {
    setForceCitySelector(false);
    setSelectedCity(city);
    setSelectedOption("mapa");
    onClose();
  };

  const handleOpenCitySelector = () => {
    setForceCitySelector(true);
    localStorage.removeItem("branding_city");
    setSelectedOption("mapa");
    onClose();
  };

  const menuItems = [
    { id: "home", label: "Início", shortLabel: "Início", icon: FaHome },
    { id: "mapa", label: "Mapa", shortLabel: "Mapa", icon: FaMapMarkedAlt },
    { id: "eventos", label: "Eventos", shortLabel: "Alertas", icon: FaBell },
    { id: "cameras", label: "Câmeras", shortLabel: "Câmeras", icon: FaVideo },
    { id: "relatorios", label: "Empresa", shortLabel: "Empresa", icon: FaBuilding },
    { id: "perfil", label: "Meu perfil", shortLabel: "Perfil", icon: FaUser }
  ];

  const primaryItems = ["home", "mapa", "eventos", "cameras", "perfil"];

  const renderContent = () => {
    if (shouldBlockUntilCityChoice) {
      if (loading) {
        return <DashboardLoader label="Preparando as cidades disponíveis..." />;
      }
      return (
        <CitySelector
          cities={availableCities}
          assetUrl={assetUrl}
          onSelectCity={handleSelectCity}
        />
      );
    }
    if (loading) {
      return <DashboardLoader label="Abrindo a cidade selecionada..." />;
    }
    if (selectedOption === "home") {
      return <Home onNavigate={handleMenuClick} />;
    }
    if (selectedOption === "mapa") {
      return <MapaCameras />;
    }
    if (selectedOption === "eventos") {
      return <Ocorrencias />;
    }
    if (selectedOption === "cameras") {
      return <GerenciarCameras usuarioId={userId} />;
    }
    if (selectedOption === "relatorios") {
      return <InformacoesEmpresa />;
    }
    if (selectedOption === "perfil") {
      return <PerfilUsuario />;
    }
    return null;
  };

  return (
    <Box
      minH="100vh"
      position="relative"
      bg="#051a12"
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      <Box
        position="fixed"
        inset="0"
        bgImage={`linear-gradient(180deg, rgba(6, 18, 13, 0.7), rgba(4, 16, 11, 0.9)), url('${dashboardBackgroundUrl}')`}
        bgSize="cover"
        bgPosition="center"
        pointerEvents="none"
        zIndex="0"
      />
      <Box
        position="fixed"
        inset="0"
        bg="radial-gradient(circle at 12% 8%, rgba(127, 238, 195, 0.16), transparent 34%), radial-gradient(circle at 88% 92%, rgba(122, 214, 255, 0.14), transparent 38%)"
        pointerEvents="none"
        zIndex="0"
      />

      <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="xs">
        <DrawerOverlay />
        <DrawerContent bg="#f8faf8">
          <DrawerHeader borderBottomWidth="1px" bg={headerBg} color="white">
            <HStack spacing={3} align="center">
              <Avatar
                size="md"
                name={user?.nome || "Usuário"}
                src={userId ? `${apiBase}/usuarios/${userId}.png` : undefined}
              />
              <Box>
                <Text fontWeight="bold">{user?.nome || "Usuário"}</Text>
                <Text fontSize="xs" color="whiteAlpha.800">
                  {user?.email || "Sem e-mail"}
                </Text>
              </Box>
            </HStack>
          </DrawerHeader>
          <DrawerBody>
            <Stack spacing={1} mt={3}>
              {shouldShowCitySelector ? (
                <>
                  <Button
                    variant="ghost"
                    colorScheme="teal"
                    justifyContent="flex-start"
                    onClick={handleOpenCitySelector}
                    borderRadius="xl"
                  >
                    Trocar cidade
                  </Button>
                  <Divider my={2} />
                </>
              ) : null}
              {canSwitchCities && availableCities.length > 0 && !shouldShowCitySelector ? (
                <>
                  <Text fontSize="xs" fontWeight="bold" color="gray.500" px={3} pt={2}>
                    Cidade
                  </Text>
                  <Select
                    size="sm"
                    borderRadius="lg"
                    value={selectedCity || ""}
                    onChange={(e) => setSelectedCity(e.target.value)}
                  >
                    {availableCities.map((city) => (
                      <option key={`${city.empresa_id}-${city.id}`} value={city.cidade}>
                        {city.cidade}{resolveCityState(city) ? ` - ${resolveCityState(city)}` : ""}
                      </option>
                    ))}
                  </Select>
                  <Divider my={2} />
                </>
              ) : null}
              {menuItems.map((item) => (
                <Button
                  key={item.id}
                  variant={selectedOption === item.id ? "solid" : "ghost"}
                  colorScheme={selectedOption === item.id ? "green" : "gray"}
                  justifyContent="flex-start"
                  leftIcon={<item.icon />}
                  onClick={() => handleMenuClick(item.id)}
                  borderRadius="xl"
                >
                  {item.label}
                </Button>
              ))}
              <Button
                variant="ghost"
                colorScheme="red"
                justifyContent="flex-start"
                leftIcon={<FaSignOutAlt />}
                onClick={handleLogout}
              >
                Sair
              </Button>
            </Stack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Box
        position="fixed"
        top={{ base: "8px", md: "14px" }}
        left="50%"
        transform="translateX(-50%)"
        width="calc(100% - 14px)"
        maxW="1180px"
        bg="rgba(238, 245, 238, 0.86)"
        backdropFilter="blur(12px)"
        border="1px solid rgba(255,255,255,0.75)"
        borderRadius="20px"
        px={{ base: 3, md: 4 }}
        py={{ base: 2, md: 3 }}
        boxShadow="0 14px 30px rgba(5, 33, 24, 0.16)"
        zIndex="1100"
      >
        <Flex align="center" justify="space-between" gap={2}>
          <IconButton
            icon={<FaBars />}
            onClick={onOpen}
            aria-label="Menu"
            borderRadius="14px"
            bg="rgba(255,255,255,0.72)"
            border="1px solid rgba(21, 42, 30, 0.16)"
            color="#203328"
            _hover={{ bg: "rgba(255,255,255,0.92)" }}
            _active={{ bg: "rgba(255,255,255,0.92)" }}
          />
          <HStack spacing={{ base: 2, md: 3 }} align="center" flex="1" justify="center" minW="0">
            <Image src={logoUrl} alt={brandName} h={{ base: "30px", md: "40px" }} objectFit="contain" />
            <Text
              color={branding.secondary_color || "#0b6530"}
              fontWeight="900"
              fontSize={{ base: "lg", md: "2xl" }}
              noOfLines={1}
            >
              {brandName}
            </Text>
          </HStack>
          <Stack minW="36px" textAlign="right" align="flex-end" spacing={1}>
            {shouldShowCitySelector && selectedCity ? (
              <Button
                size="xs"
                borderRadius="full"
                bg="rgba(25, 54, 38, 0.08)"
                color="#284636"
                border="1px solid rgba(40, 70, 54, 0.16)"
                _hover={{ bg: "rgba(25, 54, 38, 0.14)" }}
                onClick={handleOpenCitySelector}
              >
                Trocar cidade
              </Button>
            ) : null}
            <Box display={{ base: "none", sm: "block" }}>
              <Text fontSize="xs" color="#4a5f53" noOfLines={1}>
                {selectedCity || (shouldShowCitySelector ? "Escolha uma cidade" : user?.cidade || "Minha rede")}
              </Text>
            </Box>
          </Stack>
        </Flex>
      </Box>

      <Box
        flex="1"
        pt={{ base: "88px", md: "112px" }}
        pb={{ base: "82px", md: "106px" }}
        overflowX="hidden"
        minH="100vh"
        position="relative"
        zIndex="1"
      >
        {renderContent()}
      </Box>

      {!shouldBlockUntilCityChoice ? (
        <Box
          position="fixed"
          bottom={{ base: "8px", md: "12px" }}
          left="50%"
          transform="translateX(-50%)"
          width="calc(100% - 14px)"
          maxW="980px"
          bg="rgba(17, 40, 30, 0.62)"
          backdropFilter="blur(14px)"
          border="1px solid rgba(202, 233, 211, 0.26)"
          borderRadius="24px"
          boxShadow="0 14px 24px rgba(0,0,0,0.24)"
          zIndex="1100"
          overflow="hidden"
        >
          <Flex justify="space-between" px={2} py={1}>
            {primaryItems.map((itemId) => {
              const item = menuItems.find((entry) => entry.id === itemId);
              if (!item) return null;
              const isActive = selectedOption === itemId;
              return (
                <Button
                  key={item.id}
                  variant="ghost"
                  flex="1"
                  onClick={() => handleMenuClick(item.id)}
                  color={isActive ? "#ddffe6" : "rgba(225,245,233,0.72)"}
                  _hover={{ bg: "rgba(255,255,255,0.08)" }}
                  height="60px"
                  borderRadius="20px"
                  minW="0"
                  px={1}
                >
                  <Stack spacing={1} align="center">
                    <Box
                      boxSize="30px"
                      borderRadius="full"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      bg={isActive ? "rgba(255,255,255,0.18)" : "transparent"}
                      border={isActive ? "1px solid rgba(232,255,239,0.62)" : "1px solid transparent"}
                    >
                      <Box as={item.icon} fontSize="17px" />
                    </Box>
                    <Text fontSize="10px" noOfLines={1} maxW="100%" color={isActive ? "#f2fff5" : "rgba(220,239,228,0.76)"}>
                      {item.shortLabel || item.label}
                    </Text>
                  </Stack>
                </Button>
              );
            })}
          </Flex>
        </Box>
      ) : null}
    </Box>
  );
};

export default Dashboard;
